import { useState } from 'react';
import type { ProjectGraphNode, ProjectGraphResponse } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import { ChildGroupList } from './ChildGroupList';
import { CoMentionMatrix } from './CoMentionMatrix';
import { GraphSidebar } from './GraphSidebar';
import { GroupMembers } from './GroupMembers';
import { SelectionBreadcrumb } from './SelectionBreadcrumb';
import { SelectionHeader } from './SelectionHeader';
import { PartElsewhere } from './PartElsewhere';
import { nodeLabel, type GroupViewNode, type ViewNode } from './graphModel';
import { memberKindOf, type MemberKind, type SortMode } from './memberSort';

type Props = {
  graph: ProjectGraphResponse;
  nodesById: ReadonlyMap<string, ProjectGraphNode>;
  /** The visible nodes, which hold the group and project nodes the graph does not. */
  visibleNodes: ViewNode[];
  selectedId: string | null;
  /** The selection's placement chain, root first (see `pathToRoot`). */
  path: string[];
  /** The filter label of each neighbour of the selection that a filter hides. */
  hiddenFilters: ReadonlyMap<string, string>;
  /** The selected node is the focus. */
  focused: boolean;
  /** The groups the selected node shows once expanded. */
  childGroups: GroupViewNode[];
  /** The selected node can collapse (see `canCollapse`). */
  collapsible: boolean;
  onFitBranch: (id: string) => void;
  onCollapse: (id: string) => void;
  onFocus: (id: string | null) => void;
  onSelect: (id: string) => void;
  /** An ancestor picked in the breadcrumb. */
  onSelectAncestor: (id: string) => void;
  onShowAndReveal: (id: string) => void;
  /** A node anywhere in the graph (the co-mention matrix): brought into the view and selected, or made the focus. */
  onShowNode: (id: string) => void;
  onExpandGroup: (group: GroupViewNode) => void;
  onRevealMember: (group: GroupViewNode, memberId: string) => void;
  onShowMore: (groupId: string) => void;
};

/** The node kinds a document mentions: the ones with a co-mention matrix. */
const ENTITY_KINDS: ReadonlySet<string> = new Set(['engine', 'aircraft', 'part', 'reference']);

/**
 * Details of the selected node, under a breadcrumb of its path to the root: a group lists its members; any
 * other node shows its child groups below.
 * Every selection can fit its branch in the view; one that can collapse also offers Collapse (Hide shown
 * members, for a group).
 */
export function SelectionPanel({
  graph,
  nodesById,
  visibleNodes,
  selectedId,
  path,
  hiddenFilters,
  focused,
  childGroups,
  collapsible,
  onFitBranch,
  onCollapse,
  onFocus,
  onSelect,
  onSelectAncestor,
  onShowAndReveal,
  onShowNode,
  onExpandGroup,
  onRevealMember,
  onShowMore,
}: Props) {
  // The member sort chosen for each group kind; it outlives the group, so the next Parts group sorts the same way.
  const [sortByKind, setSortByKind] = useState<ReadonlyMap<MemberKind, SortMode>>(() => new Map());
  const synthetic = visibleNodes.find((n) => n.id === selectedId && (n.kind === 'group' || n.kind === 'project'));
  const selectedKind = selectedId === null ? undefined : nodesById.get(selectedId)?.kind;
  const entitySelected = selectedKind !== undefined && ENTITY_KINDS.has(selectedKind);
  const labelOf = (id: string) => {
    const node = visibleNodes.find((n) => n.id === id);
    if (!node) return id;
    return node.kind === 'group' || node.kind === 'project' ? node.label : nodeLabel(node);
  };
  const actions = selectedId !== null && (
    <>
      <Button plain onClick={() => onFitBranch(selectedId)}>
        Fit branch
      </Button>
      {collapsible && (
        <Button plain onClick={() => onCollapse(selectedId)}>
          {synthetic?.kind === 'group' ? 'Hide shown members' : 'Collapse'}
        </Button>
      )}
    </>
  );
  return (
    <div className="flex flex-col gap-3">
      <SelectionBreadcrumb path={path} labelOf={labelOf} onSelect={onSelectAncestor} />
      {synthetic?.kind === 'group' ? (
        <GroupMembers
          // A new group starts with an empty filter.
          key={synthetic.id}
          group={synthetic}
          nodesById={nodesById}
          visibleIds={new Set(visibleNodes.map((n) => n.id))}
          actions={actions}
          sort={sortByKind.get(memberKindOf(synthetic.groupKey)) ?? 'connections'}
          onSort={(mode) => setSortByKind((prev) => new Map(prev).set(memberKindOf(synthetic.groupKey), mode))}
          onReveal={(memberId) => onRevealMember(synthetic, memberId)}
          onShowMore={() => onShowMore(synthetic.id)}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {synthetic?.kind === 'project' ? (
            <div className="flex flex-col gap-3 text-sm text-zinc-900 dark:text-zinc-100">
              <SelectionHeader kind="project">{actions}</SelectionHeader>
              <div className="font-medium">{synthetic.label}</div>
            </div>
          ) : (
            <GraphSidebar
              // A new selection starts with its lists at the top and every evidence row closed.
              key={selectedId ?? ''}
              graph={graph}
              selectedId={selectedId}
              focused={focused}
              actions={actions}
              hiddenFilters={hiddenFilters}
              onFocus={onFocus}
              onSelect={onSelect}
              onShowAndReveal={onShowAndReveal}
            />
          )}
          {entitySelected && selectedId !== null && (
            // A new selection starts with the caps on. The key differs from the sidebar's, its sibling.
            <CoMentionMatrix
              key={`co-mentions:${selectedId}`}
              graph={graph}
              entityId={selectedId}
              onSelect={onShowNode}
            />
          )}
          {selectedKind === 'part' && selectedId !== null && (
            // DR-3662: the same part in other projects, from the cross-project part index. Keyed so a new part
            // starts its own query state.
            <PartElsewhere key={`elsewhere:${selectedId}`} partKey={selectedId} projectId={graph.projectId} />
          )}
          <ChildGroupList groups={childGroups} onExpand={onExpandGroup} />
        </div>
      )}
    </div>
  );
}
